import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import affirm from "@services/affirm";
import { hardwareSlug } from "@utils/routes";
import { formatPrice } from "@utils/pricing";
import { ProductModals } from "@models/product-globals.model";
import { SanitySyncProductDocument } from "@models/product.model";
import AffirmCallout from "@components/affirm-callout";

import flatMirror from "@images/mirror_flat.jpg";
import { ReactComponent as AffirmSVG } from "@images/affirm_logo_alt.svg";

import ModalContainer from "../ModalContainer";
import { ModalProps } from "../Modal.model";
import * as Styled from "./PaymentOptionsModal.styled";

interface OwnProps extends ModalProps {
  toggleModal: (e?: React.MouseEvent<HTMLDivElement>) => void;
  mounted: boolean;
  product: SanitySyncProductDocument;
  modals?: ProductModals;
  variantId?: string;
}

const PaymentOptionsModal = ({ toggleModal, modals, mounted, product, variantId = null }: OwnProps) => {
  const membership_heading = modals?.finance?.direct_purchase?.membership_heading;
  const membership_rate = modals?.finance?.direct_purchase?.membership_rate;
  const apr_text = modals?.finance?.direct_purchase?.apr_text;

  const [isDirectPay, setIsDirectPay] = useState(false);

  const { variants } = product;
  const variant = variantId ? variants.find(v => v.id == variantId) : null;
  const productPrices = {
    price: product.priceRange.minVariantPrice,
    compareAtPrice: product.compareAtPriceRange.minVariantPrice.amount
  };
  // prices should reflect either the selected variant or the minimum possible price
  const { price, compareAtPrice } = variant ? variant : productPrices;
  let priceAdjustment = 0;
  // MIR-734: Incorporate promotions
  // if (hasPromo) priceAdjustment = Number(hasPromo.item_adjustment_amount)
  const adjustedPrice = priceAdjustment > 0 ? Number(price) - priceAdjustment : Number(price);

  const formattedPrice = formatPrice(Number(price));
  const formattedCompareAt = formatPrice(Number(compareAtPrice));

  useEffect(() => {
    if (!isDirectPay && mounted && affirm) {
      setTimeout(() => {
        affirm.refreshUI();
      }, 100);
    }
  }, [isDirectPay, mounted]);

  const PriceCalc = () => {
    return (
      <>
        {formattedPrice}
        {compareAtPrice && formattedCompareAt !== formattedPrice && (
          <span>{formattedCompareAt}</span>
        )}
      </>
    );
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // enter key
    if (e.key === "Enter") setIsDirectPay(!isDirectPay);
  };

  return (
    <ModalContainer toggleModal={toggleModal} mounted={mounted} maxWidth={1180} name={'Payment Options'}>
      <Styled.ContentWrap>
        <Styled.ImageWrap>
          <Styled.Image src={flatMirror.src} alt="The Mirror" />
        </Styled.ImageWrap>

        <Styled.PaymentTypes>
          <Styled.ToggleWrap>
            <Styled.Toggle
              aria-checked={isDirectPay}
              role="switch"
              tabIndex={0}
              onClick={() => setIsDirectPay(!isDirectPay)}
              onKeyUp={handleKeypress}
              isDirectPay={isDirectPay}
            />
            <AnimatePresence mode="wait">
              {!isDirectPay && (
                <Styled.Financing
                  initial={{ opacity: 0, x: "30px" }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: "30px" }}>
                  Financing
                </Styled.Financing>
              )}

              {isDirectPay && (
                <Styled.DirectPurchase
                  initial={{ opacity: 0, x: "-30px" }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: "30px" }}>
                  Direct Purchase
                </Styled.DirectPurchase>
              )}
            </AnimatePresence>
          </Styled.ToggleWrap>

          <AnimatePresence mode="wait">
            {!isDirectPay && (
              <Styled.AffirmWrap>
                <AffirmCallout
                  className="affirm-as-low-as"
                  pageType="product"
                  learnmore={true}
                  total={adjustedPrice}
                />
                {apr_text && (
                  <Styled.Copy>
                    {apr_text} <AffirmSVG />
                  </Styled.Copy>
                )}
              </Styled.AffirmWrap>
            )}

            {isDirectPay && (
              <Styled.DirectPay
                initial={{ opacity: 0, x: "-30px" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "30px" }}>
                <Styled.Header>
                  <Styled.Heading>
                    {product.title}
                    <Styled.Price>
                      <PriceCalc />
                    </Styled.Price>
                  </Styled.Heading>
                  <Styled.Subheading alignCenter>+</Styled.Subheading>
                  {membership_heading && (
                    <Styled.Subheading>
                      {membership_heading}
                      <Styled.Copy>{membership_rate}</Styled.Copy>
                    </Styled.Subheading>
                  )}
                </Styled.Header>

                <Styled.Details>
                  <p>{product.cart_description.value}</p>

                  {apr_text && (
                    <Styled.Copy>
                      {apr_text} <AffirmSVG />
                    </Styled.Copy>
                  )}
                </Styled.Details>
              </Styled.DirectPay>
            )}
          </AnimatePresence>
        </Styled.PaymentTypes>
      </Styled.ContentWrap>

      <Styled.Footer>
        <Styled.FooterTitle>{product.title}</Styled.FooterTitle>

        <Styled.FooterPrice as="span">
          <PriceCalc />
        </Styled.FooterPrice>

        <Styled.FooterInfo>
          <Styled.FooterPriceEst>
            <AffirmCallout
              className="affirm-as-low-as"
              pageType="product"
              learnmore={false}
              total={adjustedPrice}
            />
          </Styled.FooterPriceEst>
          <Styled.ShopCta href={hardwareSlug}>Shop The Mirror</Styled.ShopCta>
        </Styled.FooterInfo>
      </Styled.Footer>
    </ModalContainer>
  );
};

export default PaymentOptionsModal;
